if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initYurchakColorGallery();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initYurchakColorGallery() {
        const dataNode = this.querySelector('[data-yurchak-color-gallery]');
        if (!dataNode) return;

        try {
          this.yurchakColorGallery = JSON.parse(dataNode.textContent || '{}');
        } catch (error) {
          console.warn('YURCHAK color gallery map could not be parsed.', error);
          return;
        }

        requestAnimationFrame(() => {
          const active = this.elements.viewer?.querySelector('[data-media-id].is-active')
            || this.elements.viewer?.querySelector('[data-media-id]');
          if (active?.dataset.mediaId) this.filterByVariantMedia(active.dataset.mediaId);
        });
      }

      filterByVariantMedia(mediaId) {
        const map = this.yurchakColorGallery;
        if (!map || !Array.isArray(map.order) || !Array.isArray(map.variants) || !mediaId) return;

        const selectedVariant = map.variants.find((variant) => variant.media === mediaId && variant.color);
        if (!selectedVariant) return;

        const startsByColor = new Map();
        map.variants.forEach((variant) => {
          if (!variant?.color || !variant?.media || startsByColor.has(variant.color)) return;
          const index = map.order.indexOf(variant.media);
          if (index >= 0) startsByColor.set(variant.color, index);
        });

        const starts = Array.from(startsByColor.entries())
          .map(([color, index]) => ({ color, index }))
          .sort((a, b) => a.index - b.index);

        const selectedStartEntry = starts.find((entry) => entry.color === selectedVariant.color);
        if (!selectedStartEntry) return;

        const selectedPosition = starts.indexOf(selectedStartEntry);
        const start = selectedPosition === 0 ? 0 : selectedStartEntry.index;
        const end = selectedPosition >= 0 && selectedPosition < starts.length - 1
          ? starts[selectedPosition + 1].index
          : map.order.length;

        const allowed = new Set(map.order.slice(start, end));
        if (!allowed.size) return;

        this.elements.viewer?.querySelectorAll('li[data-media-id]').forEach((item) => {
          item.hidden = !allowed.has(item.dataset.mediaId);
        });

        this.elements.thumbnails?.querySelectorAll('li[data-target]').forEach((item) => {
          item.hidden = !allowed.has(item.dataset.target);
        });

        const visibleViewerItems = Array.from(
          this.elements.viewer?.querySelectorAll('li[data-media-id]:not([hidden])') || []
        );
        const counterTotal = this.elements.viewer?.querySelector('.slider-counter--total');
        if (counterTotal) counterTotal.textContent = String(visibleViewerItems.length);

        const currentActive = this.elements.viewer?.querySelector('li[data-media-id].is-active');
        if (!currentActive || currentActive.hidden || !allowed.has(currentActive.dataset.mediaId)) {
          const preferred = visibleViewerItems.find((item) => item.dataset.mediaId === mediaId) || visibleViewerItems[0];
          if (preferred?.dataset.mediaId) this.setActiveMedia(preferred.dataset.mediaId, false);
        }

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Variant-driven media changes should never move the page. Manual gallery navigation keeps Dawn's original behavior.
          if (prepend || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        const isVertical = slider.scrollHeight > slider.clientHeight + 4;
        if (isVertical) {
          const itemTop = thumbnail.offsetTop;
          const itemBottom = itemTop + thumbnail.offsetHeight;
          const visibleTop = slider.scrollTop;
          const visibleBottom = visibleTop + slider.clientHeight;
          const padding = 10;

          if (itemTop < visibleTop + padding) {
            slider.scrollTo({ top: Math.max(0, itemTop - padding), behavior: 'smooth' });
          } else if (itemBottom > visibleBottom - padding) {
            slider.scrollTo({
              top: Math.max(0, itemBottom - slider.clientHeight + padding),
              behavior: 'smooth',
            });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
